import { CouponsListResponseBaseArray, useCouponsList } from '@/api/skippay/couponsList';
import { SearchFormValues } from './useFormSearch';
import { QueryFilter } from '@/utils/query';
import { type BreadcrumbItem } from '@/components/Breadcrumbs/Breadcrumbs';
import { useTranslation } from 'react-i18next';
import { LINKS } from '@/constants/links';

import { type Category } from '@/sections/discounts/functions/getCategories';
import { SELECT_ALL_VALUE } from '@/hooks/searchParamParsers';
import { useEffect, useRef } from 'react';
import { logEvent } from '@/analytics/events';

interface Props {
	categories?: Category[];
	formValues?: Partial<SearchFormValues>;
	limit?: number;
}

export function useDiscountsSearch(props: Props) {
	const { categories, formValues, limit } = props;
	let filter = new QueryFilter();
	const { t } = useTranslation(['discounts']);

	if (formValues?.category && formValues.category !== SELECT_ALL_VALUE) {
		filter = filter.prop('category').eq(formValues.category);
	}

	if (formValues?.fulltext) {
		filter = filter.prop('fulltext').fulltext(formValues.fulltext);
	}
	const hasFilter = filter.conditions.length > 0 || formValues === undefined;

	const query = useCouponsList(
		{
			filter: hasFilter ? filter.toString() : undefined,
			limit: limit ?? 16,
		},
		{
			enabled: hasFilter,
		}
	);

	const breadcrumbs: BreadcrumbItem[] = [];

	if (categories && formValues?.category && formValues.category !== SELECT_ALL_VALUE) {
		breadcrumbs.push({
			title: t('discounts:search.categoryBreadcrumb'),
			to: LINKS().discounts,
		});

		const category = categories.find((category) => category.id === formValues.category);

		if (category) {
			breadcrumbs.push({
				title: category.name,
				to: LINKS().discountsCategory(category.slug),
			});
		}
	}

	if (formValues?.fulltext) {
		breadcrumbs.push({
			title: t('discounts:search.fulltextBreadcrumb', { query: formValues.fulltext }),
		});
	}

	const previousData = useRef<CouponsListResponseBaseArray>([]);
	useEffect(() => {
		const fetchedData = query.data?.pages.flatMap((subArray) => subArray!.data);
		const difference = fetchedData?.filter((coupon) => !previousData.current?.includes(coupon));
		if (!query.isFetching && difference && difference.length > 0) {
			const items = difference?.map((coupon, index) => ({
				promotion_id: 'affil_promo',
				promotion_name: coupon.merchant.name,
				creative_name: coupon.merchant.name,
				creative_slot: `coupons_${index + (previousData?.current?.length || 0) + 1}`,
				location_id: coupon.url,
			}));
			logEvent('view_promotion', {
				ecommerce: {
					items,
				},
			});
		}
		previousData.current = [...previousData.current, ...(difference || [])];
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [query.data]);

	return { query, hasFilter, breadcrumbs };
}
